import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, Table } from 'react-bootstrap'
import { BoxArrowUpRight, Printer, XCircleFill } from 'react-bootstrap-icons'
import { useLocation, useNavigate } from 'react-router-dom'

import HoverOverlay from '../../components/_atoms/HoverOverlay/HoverOverlay'
import ListNoItems from '../../components/_atoms/ListNoItems/ListNoItems'
import ListPagination from '../../components/_atoms/ListPagination/ListPagination'
import ListSkeleton from '../../components/_atoms/ListSkeleton/ListSkeleton'
import SalesButton from '../../components/_atoms/SalesButton/SalesButton'
import SalesText from '../../components/_atoms/SalesText/SalesText'
import SortIcon from '../../components/_atoms/SortIcon/SortIcon'
import { colors, fontSizes, fontWeights } from '../../constants/salesStyles'
import loadInventoryFacets from '../../modules/redux/inventory/LoadInventoryFacets'
import loadInventoryPartList from '../../modules/redux/inventory/LoadInventoryPartList'
import printInventoryPartList from '../../modules/redux/inventory/PrintInventoryPartList'
import updateInventoryPartQty from '../../modules/redux/inventory/UpdateInventoryPartQty'
import { useDispatch } from '../../modules/store/customDispatch'
import { useSelector } from '../../modules/store/customSelector'
import { storeDispatchTypes } from '../../modules/store/storeDispatchTypes'
import { SortIconColor, SortIconType, SortOrder } from '../../types/common/commonTypes'
import {
    InventoryFacetOption,
    InventoryFilters,
    InventoryPartListItem,
    InventorySortField,
} from '../../types/inventory/inventoryTypes'

import styles from './styles.module.scss'

const getInitialFilter = (params: URLSearchParams, name: keyof InventoryFilters) => params.get(name) || ''

const buildQueryString = (
    filters: InventoryFilters,
    page: number,
    perPage: number,
    order: [InventorySortField, SortOrder][],
) => {
    const params = new URLSearchParams()

    Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value)
    })

    if (page > 1) params.set('page', String(page))
    if (perPage !== 50) params.set('perPage', String(perPage))
    if (order.length) params.set('order', order.map(item => item.join(':')).join(','))

    return params.toString()
}

const parseOrder = (value: string | null): [InventorySortField, SortOrder][] => {
    if (!value) return []

    return value
        .split(',')
        .map(item => item.split(':'))
        .filter(item => item.length === 2)
        .map(([field, direction]) => [field as InventorySortField, direction as SortOrder])
}

const getNextSortOrder = (
    currentOrder: [InventorySortField, SortOrder][],
    field: InventorySortField,
): [InventorySortField, SortOrder][] => {
    const current = currentOrder.find(([sortField]) => sortField === field)

    if (!current) return [[field, SortOrder.ASC]]
    if (current[1] === SortOrder.ASC) return [[field, SortOrder.DESC]]

    return []
}

const getInventorySortIconType = (field: InventorySortField): SortIconType => {
    if (['vendorCode', 'warehouseRow', 'warehouseShelf', 'warehousePlace', 'qty', 'updatedAt'].includes(field)) {
        return SortIconType.NUMERIC
    }

    return SortIconType.ALPHABETIC
}

type InventoryTableColumnField = Exclude<InventorySortField, 'updatedAt'> | 'actions'

const inventoryColumnWidths: Record<InventoryTableColumnField, number> = {
    vendorCode: 100,
    title: 400,
    warehouseTitle: 150,
    warehouseRow: 80,
    warehouseShelf: 80,
    warehousePlace: 80,
    warehouseSection: 150,
    qty: 100,
    actions: 100,
}

const inventoryTableWidth = Object.values(inventoryColumnWidths).reduce((sum, width) => sum + width, 0)
const getInventoryColumnStyle = (width: number) => ({ width: `${(width / inventoryTableWidth) * 100}%` })

const InventoryPage = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const [isUrlLoaded, setIsUrlLoaded] = useState(false)
    const [qtyDrafts, setQtyDrafts] = useState<Record<string, number>>({})
    const [debouncedTextFilters, setDebouncedTextFilters] = useState<Pick<InventoryFilters, 'search' | 'warehouseSection'>>(
        {
            search: '',
            warehouseSection: '',
        },
    )
    const isFirstTextFiltersSync = useRef(true)

    const {
        data,
        dataCount,
        facets,
        filters,
        isFacetsLoading,
        isLoading,
        isPrinting,
        order,
        pagination: { page, perPage },
    } = useSelector(state => state.inventory)

    useEffect(() => {
        if (isUrlLoaded) return

        const params = new URLSearchParams(location.search)
        const nextFilters: InventoryFilters = {
            search: getInitialFilter(params, 'search'),
            warehouseId: getInitialFilter(params, 'warehouseId'),
            warehouseRow: getInitialFilter(params, 'warehouseRow'),
            warehouseShelf: getInitialFilter(params, 'warehouseShelf'),
            warehousePlace: getInitialFilter(params, 'warehousePlace'),
            warehouseSection: getInitialFilter(params, 'warehouseSection'),
        }

        dispatch({
            type: storeDispatchTypes.setInventoryFilter,
            value: nextFilters,
        })
        dispatch({
            type: storeDispatchTypes.setInventoryPagination,
            value: {
                page: Number(params.get('page') || 1),
                perPage: Number(params.get('perPage') || 50),
            },
        })
        dispatch({
            type: storeDispatchTypes.setInventorySort,
            value: parseOrder(params.get('order')),
        })
        setDebouncedTextFilters({
            search: nextFilters.search,
            warehouseSection: nextFilters.warehouseSection,
        })
        isFirstTextFiltersSync.current = true
        setIsUrlLoaded(true)
    }, [dispatch, isUrlLoaded, location.search])

    useEffect(() => {
        if (!isUrlLoaded) return

        if (isFirstTextFiltersSync.current) {
            isFirstTextFiltersSync.current = false
            setDebouncedTextFilters({
                search: filters.search,
                warehouseSection: filters.warehouseSection,
            })
            return
        }

        const timeout = window.setTimeout(() => {
            setDebouncedTextFilters({
                search: filters.search,
                warehouseSection: filters.warehouseSection,
            })
        }, 1000)

        return () => window.clearTimeout(timeout)
    }, [filters.search, filters.warehouseSection, isUrlLoaded])

    const effectiveFilters = useMemo(
        () => ({
            warehouseId: filters.warehouseId,
            warehouseRow: filters.warehouseRow,
            warehouseShelf: filters.warehouseShelf,
            warehousePlace: filters.warehousePlace,
            search: debouncedTextFilters.search,
            warehouseSection: debouncedTextFilters.warehouseSection,
        }),
        [
            debouncedTextFilters.search,
            debouncedTextFilters.warehouseSection,
            filters.warehouseId,
            filters.warehousePlace,
            filters.warehouseRow,
            filters.warehouseShelf,
        ],
    )

    const requestParams = useMemo(
        () => ({
            ...effectiveFilters,
            pagination: {
                page,
                perPage,
            },
            order,
        }),
        [effectiveFilters, page, perPage, order],
    )

    useEffect(() => {
        if (!isUrlLoaded) return

        const query = buildQueryString(filters, page, perPage, order)
        navigate(query ? `/admin/inventory?${query}` : '/admin/inventory', {
            replace: true,
        })
    }, [filters, isUrlLoaded, navigate, order, page, perPage])

    useEffect(() => {
        if (!isUrlLoaded) return

        dispatch(loadInventoryPartList(requestParams)).then(isLoaded => {
            if (isLoaded) {
                dispatch(loadInventoryFacets(effectiveFilters))
            }
        })
    }, [dispatch, effectiveFilters, isUrlLoaded, requestParams])

    useEffect(() => {
        setQtyDrafts(data.reduce((acc, item) => ({ ...acc, [item.id]: item.qty }), {}))
    }, [data])

    const setFilter = (patch: Partial<InventoryFilters>) => {
        dispatch({ type: storeDispatchTypes.setInventoryFilter, value: patch })
    }

    const setPage = (nextPage: number) => {
        dispatch({
            type: storeDispatchTypes.setInventoryPagination,
            value: { page: nextPage },
        })
    }

    const setSort = (field: InventorySortField) => {
        dispatch({
            type: storeDispatchTypes.setInventorySort,
            value: getNextSortOrder(order, field),
        })
    }

    const resetSort = () => {
        dispatch({
            type: storeDispatchTypes.setInventorySort,
            value: [],
        })
    }

    const printList = () => {
        dispatch(printInventoryPartList({ ...filters, order }))
    }

    const resetFilters = () => {
        dispatch({ type: storeDispatchTypes.resetInventoryFilters })
    }

    const onQtyKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (
            !(
                (event.key >= '0' && event.key <= '9') ||
                event.key === 'Backspace' ||
                event.key === 'Delete' ||
                event.key.includes('Arrow') ||
                event.key === 'Tab' ||
                event.key === 'Enter'
            )
        ) {
            event.preventDefault()
        }

        if (event.key === 'Enter') {
            event.currentTarget.blur()
        }
    }

    const saveQty = async (part: InventoryPartListItem) => {
        const qty = qtyDrafts[part.id]
        if (qty === part.qty) return

        const isSaved = await dispatch(updateInventoryPartQty(part.id, qty))
        if (!isSaved) {
            setQtyDrafts(current => ({ ...current, [part.id]: part.qty }))
        }
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.header}>
                <SalesText
                    text="Инвентаризация"
                    fontWeight={fontWeights.bold}
                    fontSize={fontSizes.xxl}
                    letterSpacing={-0.02}
                />
                <SalesText text={`В выборке: ${dataCount}`} fontSize={fontSizes.s} color={colors.grayDark} />
                <SalesButton
                    borderRadius={10}
                    backgroundColor={colors.blueLight}
                    paddingHorizontalPixels={20}
                    paddingVerticalPixels={6}
                    onClick={printList}
                    disabled={isPrinting || isLoading}
                >
                    <>
                        <Printer size={20} color={colors.white} />
                        <SalesText
                            text={isPrinting ? 'Формируется' : 'Печать'}
                            marginLeftPixels={10}
                            fontSize={fontSizes.s}
                            color={colors.white}
                        />
                    </>
                </SalesButton>
                <div className={styles.paginationTop}>
                    <ListPagination page={page} setPage={setPage} perPage={perPage} itemsCount={dataCount} />
                </div>
            </div>

            <div className={styles.tableWrap}>
                <Table striped hover className={styles.table} style={{ width: '100%', minWidth: inventoryTableWidth }}>
                    <colgroup>
                        <col style={getInventoryColumnStyle(inventoryColumnWidths.vendorCode)} />
                        <col style={getInventoryColumnStyle(inventoryColumnWidths.title)} />
                        <col style={getInventoryColumnStyle(inventoryColumnWidths.warehouseTitle)} />
                        <col style={getInventoryColumnStyle(inventoryColumnWidths.warehouseRow)} />
                        <col style={getInventoryColumnStyle(inventoryColumnWidths.warehouseShelf)} />
                        <col style={getInventoryColumnStyle(inventoryColumnWidths.warehousePlace)} />
                        <col style={getInventoryColumnStyle(inventoryColumnWidths.warehouseSection)} />
                        <col style={getInventoryColumnStyle(inventoryColumnWidths.qty)} />
                        <col style={getInventoryColumnStyle(inventoryColumnWidths.actions)} />
                    </colgroup>
                    <thead>
                    <tr className={styles.filterRow}>
                        <th scope="col" colSpan={2} className={styles.filterCell}>
                            <Form.Control
                                className={styles.filterControl}
                                value={filters.search}
                                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                    setFilter({ search: event.target.value })
                                }
                                placeholder="Поиск"
                            />
                        </th>
                        <th scope="col" className={styles.filterCell}>
                            <FacetSelect
                                value={filters.warehouseId}
                                emptyLabel={isFacetsLoading ? 'Загрузка...' : 'Все склады'}
                                onChange={warehouseId => setFilter({ warehouseId })}
                                options={facets.warehouses.map(item => ({
                                    value: item.id === null ? 'null' : item.id,
                                    title: `${item.title} (${item.count})`,
                                }))}
                            />
                        </th>
                        <th scope="col" className={styles.filterCell}>
                            <FacetSelect
                                value={filters.warehouseRow}
                                emptyLabel={isFacetsLoading ? 'Загрузка...' : 'Ряд'}
                                onChange={warehouseRow => setFilter({ warehouseRow })}
                                options={mapFacetOptions(facets.rows)}
                            />
                        </th>
                        <th scope="col" className={styles.filterCell}>
                            <FacetSelect
                                value={filters.warehouseShelf}
                                emptyLabel={isFacetsLoading ? 'Загрузка...' : 'Полка'}
                                onChange={warehouseShelf => setFilter({ warehouseShelf })}
                                options={mapFacetOptions(facets.shelves)}
                            />
                        </th>
                        <th scope="col" className={styles.filterCell}>
                            <FacetSelect
                                value={filters.warehousePlace}
                                emptyLabel={isFacetsLoading ? 'Загрузка...' : 'Место'}
                                onChange={warehousePlace => setFilter({ warehousePlace })}
                                options={mapFacetOptions(facets.places)}
                            />
                        </th>
                        <th scope="col" className={styles.filterCell}>
                            <Form.Control
                                className={styles.filterControl}
                                value={filters.warehouseSection}
                                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                    setFilter({ warehouseSection: event.target.value })
                                }
                                placeholder="Секция"
                            />
                        </th>
                        <th scope="col" colSpan={2} className={styles.filterCell}>
                            <SalesButton
                                className={styles.resetButton}
                                borderRadius={6}
                                hoverBackgroundColor={colors.grayLighter}
                                paddingHorizontalPixels={10}
                                paddingVerticalPixels={5}
                                onClick={resetFilters}
                            >
                                <SalesText text="Сбросить" fontSize={fontSizes.xs} />
                            </SalesButton>
                        </th>
                    </tr>
                    <tr>
                        <SortableTh
                            title="Артикул"
                            field="vendorCode"
                            order={order}
                            setSort={setSort}
                            resetSort={resetSort}
                            widthStyle={getInventoryColumnStyle(inventoryColumnWidths.vendorCode)}
                        />
                        <SortableTh
                            title="Название запчасти"
                            field="title"
                            order={order}
                            setSort={setSort}
                            resetSort={resetSort}
                            widthStyle={getInventoryColumnStyle(inventoryColumnWidths.title)}
                        />
                        <SortableTh
                            title="Склад"
                            field="warehouseTitle"
                            order={order}
                            setSort={setSort}
                            resetSort={resetSort}
                            widthStyle={getInventoryColumnStyle(inventoryColumnWidths.warehouseTitle)}
                        />
                        <SortableTh
                            title="Ряд"
                            field="warehouseRow"
                            order={order}
                            setSort={setSort}
                            resetSort={resetSort}
                            widthStyle={getInventoryColumnStyle(inventoryColumnWidths.warehouseRow)}
                        />
                        <SortableTh
                            title="Полка"
                            field="warehouseShelf"
                            order={order}
                            setSort={setSort}
                            resetSort={resetSort}
                            widthStyle={getInventoryColumnStyle(inventoryColumnWidths.warehouseShelf)}
                        />
                        <SortableTh
                            title="Место"
                            field="warehousePlace"
                            order={order}
                            setSort={setSort}
                            resetSort={resetSort}
                            widthStyle={getInventoryColumnStyle(inventoryColumnWidths.warehousePlace)}
                        />
                        <SortableTh
                            title="Секция"
                            field="warehouseSection"
                            order={order}
                            setSort={setSort}
                            resetSort={resetSort}
                            widthStyle={getInventoryColumnStyle(inventoryColumnWidths.warehouseSection)}
                        />
                        <SortableTh
                            title="Остаток"
                            field="qty"
                            order={order}
                            setSort={setSort}
                            resetSort={resetSort}
                            widthStyle={getInventoryColumnStyle(inventoryColumnWidths.qty)}
                        />
                        <th
                            scope="col"
                            style={getInventoryColumnStyle(inventoryColumnWidths.actions)}
                            className={styles.actionTh}
                        >
                            Действие
                        </th>
                    </tr>
                    </thead>
                    {isLoading ? (
                        <ListSkeleton rowCount={10} columnCount={9} marginVerticalRem={0.21} />
                    ) : (
                        <tbody>
                            {data.map(part => (
                                <tr key={part.id}>
                                    <td>{`${part.vendorCodePrefix || ''}${part.vendorCode || ''}`}</td>
                                    <td>{part.customTitle || part.title}</td>
                                    <td>{part.warehouseTitle}</td>
                                    <td>{part.warehouseRow}</td>
                                    <td>{part.warehouseShelf}</td>
                                    <td>{part.warehousePlace}</td>
                                    <td>{part.warehouseSection}</td>
                                    <td>
                                        <Form.Control
                                            className={styles.qtyInput}
                                            value={qtyDrafts[part.id] ?? part.qty}
                                            size="sm"
                                            type="text"
                                            onKeyDown={onQtyKeyDown}
                                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                                setQtyDrafts(current => ({
                                                    ...current,
                                                    [part.id]: Number(event.target.value),
                                                }))
                                            }
                                            onBlur={() => saveQty(part)}
                                            style={
                                                (qtyDrafts[part.id] ?? part.qty) === 0
                                                    ? { backgroundColor: '#f8d7da' }
                                                    : undefined
                                            }
                                        />
                                    </td>
                                    <td className={styles.actionCell}>
                                        <HoverOverlay tooltipText="Открыть в новом окне" preventWrapFullWidth>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() =>
                                                    window.open(`/admin/parts/${part.id}`, '_blank', 'noopener,noreferrer')
                                                }
                                                className={styles.openPartButton}
                                            >
                                                <BoxArrowUpRight color={colors.white} size={14} />
                                            </Button>
                                        </HoverOverlay>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    )}
                </Table>
            </div>

            {!isLoading && data.length === 0 ? (
                <ListNoItems message="По выбранным фильтрам остатков нет" resetFilters={resetFilters} />
            ) : (
                <ListPagination
                    page={page}
                    setPage={setPage}
                    perPage={perPage}
                    itemsCount={dataCount}
                    justifyContent="flex-end"
                />
            )}
        </div>
    )
}

type FacetSelectProps = {
    value: string
    emptyLabel: string
    options: Array<{ value: string; title: string }>
    onChange: (value: string) => void
}

const FacetSelect = ({ value, emptyLabel, options, onChange }: FacetSelectProps) => (
    <Form.Select className={styles.filterControl} value={value} onChange={event => onChange(event.target.value)}>
        <option value="">{emptyLabel}</option>
        {options.map(option => (
            <option key={option.value} value={option.value}>
                {option.title}
            </option>
        ))}
    </Form.Select>
)

const mapFacetOptions = (options: InventoryFacetOption[]) =>
    options.map(item => ({
        value: item.value,
        title: `${item.value} (${item.count})`,
    }))

type SortableThProps = {
    title: string
    field: InventorySortField
    order: [InventorySortField, SortOrder][]
    setSort: (field: InventorySortField) => void
    resetSort: () => void
    widthStyle: { width: string }
}

const SortableTh = ({ title, field, order, setSort, resetSort, widthStyle }: SortableThProps) => {
    const current = order.find(([sortField]) => sortField === field)
    const currentSortOrder = current ? current[1] : SortOrder.NONE
    const isActive = currentSortOrder !== SortOrder.NONE
    const iconColor = isActive ? SortIconColor.ACTIVE : SortIconColor.INACTIVE

    return (
        <th scope="col" style={widthStyle} onClick={() => setSort(field)} className={styles.sortableTh}>
            <span className={styles.sortableTitle}>
                <span className={styles.sortTitleText}>{title}</span>
                <span className={styles.sortIcons}>
                    <SortIcon
                        iconType={getInventorySortIconType(field)}
                        sortOrder={currentSortOrder}
                        color={iconColor}
                        size={16}
                        reverseSort={field === 'vendorCode' || field === 'updatedAt'}
                    />
                    {isActive && (
                        <XCircleFill
                            className={styles.clearSortIcon}
                            color="grey"
                            size={12}
                            onClick={event => {
                                event.stopPropagation()
                                resetSort()
                            }}
                        />
                    )}
                </span>
            </span>
        </th>
    )
}

export default InventoryPage
